/**
 * InlineDatePicker Component
 *
 * Date picker with calendar popup and optional time selection.
 */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isToday,
  isSameMonth,
  isSameDay,
  startOfWeek,
  endOfWeek,
  startOfDay,
} from 'date-fns';
import { it } from 'date-fns/locale';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';

/**
 * Normalize Date or timestamp to Date
 */
function normalizeDate(date: Date | number | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  return null;
}

interface CalendarDatePickerProps {
  value: Date | number | null;
  onChange: (date: Date | null) => void;
  showTime?: boolean;
  disabled?: boolean;
}

function CalendarDatePicker({
  value,
  onChange,
  showTime = false,
  disabled = false,
}: CalendarDatePickerProps) {
  const normalizedValue = normalizeDate(value);

  const [currentMonth, setCurrentMonth] = useState(() => normalizedValue || new Date());
  const [selectedTime, setSelectedTime] = useState(() => {
    if (normalizedValue && showTime) {
      return format(normalizedValue, 'HH:mm');
    }
    return '09:00';
  });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { locale: it });
  const calendarEnd = endOfWeek(monthEnd, { locale: it });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handleDateSelect = (day: Date) => {
    if (disabled) return;
    const selectedDate = startOfDay(day);
    if (showTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      selectedDate.setHours(hours, minutes);
    }
    onChange(selectedDate);
  };

  const handleTimeChange = (time: string) => {
    if (normalizedValue) {
      const [hours, minutes] = time.split(':').map(Number);
      const newDate = new Date(normalizedValue);
      newDate.setHours(hours, minutes);
      onChange(newDate);
    }
    setSelectedTime(time);
  };

  const handleClear = () => {
    onChange(null);
  };

  const handleToday = () => {
    const today = new Date();
    if (showTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      today.setHours(hours, minutes);
    }
    onChange(today);
  };

  return (
    <div className="p-3 bg-surface-primary rounded-lg border border-border-primary shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentMonth((prev) => subMonths(prev, 1))}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
            disabled={disabled}
          >
            <ChevronLeft className="w-4 h-4 text-text-secondary" />
          </button>
          <span className="text-[12px] font-semibold text-text-primary min-w-[120px] text-center">
            {format(currentMonth, 'MMMM yyyy', { locale: it })}
          </span>
          <button
            onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
            disabled={disabled}
          >
            <ChevronRight className="w-4 h-4 text-text-secondary" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleToday}
            className="px-2 py-1 text-[10px] rounded hover:bg-surface-hover transition-colors text-text-secondary"
            disabled={disabled}
          >
            Oggi
          </button>
          {normalizedValue && (
            <button
              onClick={handleClear}
              className="p-1 rounded hover:bg-surface-hover transition-colors"
              disabled={disabled}
            >
              <X className="w-3.5 h-3.5 text-text-quaternary" />
            </button>
          )}
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((day, i) => (
          <div key={i} className="text-[10px] font-semibold text-text-quaternary text-center py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = normalizedValue && isSameDay(day, normalizedValue);
          const isTodayDate = isToday(day);

          return (
            <button
              key={i}
              onClick={() => handleDateSelect(day)}
              disabled={disabled || !isCurrentMonth}
              className={clsx(
                'aspect-square rounded-md text-[11px] font-medium transition-all',
                !isCurrentMonth && 'opacity-30',
                isSelected
                  ? 'bg-accent-violet text-white'
                  : isTodayDate
                    ? 'bg-accent-violet/10 text-accent-violet font-semibold'
                    : 'hover:bg-surface-hover text-text-secondary',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Time Picker */}
      {showTime && (
        <div className="mt-3 pt-3 border-t border-border-primary/50">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-text-quaternary" />
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="px-2 py-1 text-[11px] bg-surface-tertiary border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary"
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export interface InlineDatePickerProps {
  value: Date | number | null | undefined;
  onChange: (date: number | null) => void;
  label?: string;
  showTime?: boolean;
  size?: 'sm' | 'md';
  disabled?: boolean;
  highlight?: boolean;
}

export function InlineDatePicker({
  value,
  onChange,
  label,
  showTime = true,
  size = 'md',
  disabled = false,
  highlight = false,
}: InlineDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 8,
    minWidth: 280,
    matchTriggerWidth: false,
  });

  const normalizedValue = normalizeDate(value);

  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  const formatDisplayDate = (date: Date | null) => {
    if (!date) return label || 'Imposta data';
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      ...(showTime && { hour: '2-digit', minute: '2-digit' }),
    });
  };

  const handleDateChange = (date: Date | null) => {
    onChange(date ? date.getTime() : null);
    if (!showTime) {
      setIsOpen(false);
    }
  };

  if (disabled) {
    return (
      <span
        className={clsx(
          'flex items-center gap-1.5',
          size === 'sm' ? 'text-[10px]' : 'text-[11px]',
          highlight ? 'text-accent-rose font-medium' : 'text-text-tertiary'
        )}
      >
        <Calendar className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
        {formatDisplayDate(normalizedValue)}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={clsx(
          'flex items-center gap-1.5 rounded-md transition-all bg-surface-tertiary hover:bg-surface-hover whitespace-nowrap',
          size === 'sm'
            ? 'px-2.5 h-6 text-[10px]'
            : 'px-2 h-7 text-[11px]',
          highlight
            ? 'text-accent-rose font-medium'
            : normalizedValue
              ? 'text-text-secondary'
              : 'text-text-quaternary'
        )}
        title="Clicca per modificare la data"
      >
        <Calendar className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5', 'shrink-0')} />
        <span className="truncate">{formatDisplayDate(normalizedValue)}</span>
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="z-50 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarDatePicker
              value={normalizedValue}
              onChange={handleDateChange}
              showTime={showTime}
            />
          </div>,
          document.body
        )}
    </>
  );
}
